// src/api/user.js
import API from "./api";

const FOLLOWING_IDS_CACHE_PREFIX = "atoz_user_following_v1_";
const FOLLOWING_IDS_CACHE_TTL_MS = 10 * 60 * 1000;

const readFollowingIdsCache = (userId) => {
  try {
    const raw = localStorage.getItem(`${FOLLOWING_IDS_CACHE_PREFIX}${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.ids)) return null;
    if (Date.now() - Number(parsed.timestamp || 0) > FOLLOWING_IDS_CACHE_TTL_MS) return null;
    return parsed.ids.map((id) => String(id).trim()).filter(Boolean);
  } catch {
    return null;
  }
};

const writeFollowingIdsCache = (userId, ids = []) => {
  try {
    localStorage.setItem(
      `${FOLLOWING_IDS_CACHE_PREFIX}${userId}`,
      JSON.stringify({ timestamp: Date.now(), ids })
    );
  } catch {
    // ignore storage errors
  }
};

const readFollowingIdsCacheAny = (userId) => {
  try {
    const raw = localStorage.getItem(`${FOLLOWING_IDS_CACHE_PREFIX}${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.ids)) return null;
    return parsed.ids.map((id) => String(id).trim()).filter(Boolean);
  } catch {
    return null;
  }
};

/** Lightweight following lookup — avoids heavy /user/:id profile+news query. */
export const getUserFollowingIds = async (userId, options = {}) => {
  const { forceRefresh = false } = options;
  const normalizedId = String(userId || "").trim();
  if (!/^[0-9a-fA-F]{24}$/.test(normalizedId)) return [];

  if (!forceRefresh) {
    const cached = readFollowingIdsCache(normalizedId);
    if (cached) return cached;
  }

  try {
    const res = await API.post(
      "/users/by-ids",
      { ids: [normalizedId] },
      { timeout: 8000 }
    );
    const user = Array.isArray(res?.data) ? res.data[0] : null;
    const ids = Array.isArray(user?.following)
      ? user.following.map((id) => String(id).trim()).filter(Boolean)
      : [];
    writeFollowingIdsCache(normalizedId, ids);
    return ids;
  } catch (error) {
    const stale = readFollowingIdsCacheAny(normalizedId);
    if (stale) return stale;
    console.warn("Error fetching user following list:", error?.message || error);
    return [];
  }
};

export const updateUserFollowingIdsCache = (userId, followingIds = []) => {
  const normalizedId = String(userId || "").trim();
  if (!normalizedId) return;
  writeFollowingIdsCache(
    normalizedId,
    followingIds.map((id) => String(id).trim()).filter(Boolean)
  );
};

/** Keep following cache in sync after follow/unfollow so UI panels refresh correctly. */
export const patchFollowingIdsCache = (currentUserId, targetUserId, isFollowing) => {
  const uid = String(currentUserId || "").trim();
  const tid = String(targetUserId || "").trim();
  if (!uid || !tid) return;

  const existing = readFollowingIdsCacheAny(uid) || [];
  const next = new Set(existing.map((id) => String(id).trim()).filter(Boolean));
  if (isFollowing) next.add(tid);
  else next.delete(tid);
  writeFollowingIdsCache(uid, [...next]);
};

// Get user by ID
export const getUserById = async (id) => {
  if (id === null || id === undefined)
    throw new Error("targetUserId is required");
  const res = await API.get(`/user/${id}`);
  return res.data;
};

export const getUsersByIds = async (ids) => {
  if (!Array.isArray(ids)) {
    throw new Error("IDs should be an array");
  }

  const normalizedIds = ids
    .map((value) => {
      if (typeof value === "string" || typeof value === "number") {
        return String(value).trim();
      }

      if (value && typeof value === "object") {
        return String(value._id || value.id || "").trim();
      }

      return "";
    })
    .filter((value) => /^[0-9a-fA-F]{24}$/.test(value));

  if (normalizedIds.length === 0) {
    return [];
  }

  const res = await API.post("/users/by-ids", { ids: normalizedIds });
  return res.data;
};

// Follow user
export const followUser = async (userId, token, currentUserId = "") => {
  try {
    const response = await API.post(
      `/user/${userId}/follow`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (currentUserId) {
      patchFollowingIdsCache(currentUserId, userId, true);
    }
    return response.data;
  } catch (error) {
    console.error("Error following user:", error);
    throw error;
  }
};

// Preferred template (per current user)
export const getPreferredTemplate = async (token) => {
  const res = await API.get('/users/me/preferred-template', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data; // { preferredTemplate }
};

export const setPreferredTemplate = async (templateId, token) => {
  const res = await API.put(
    '/users/me/preferred-template',
    { templateId },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data; // { preferredTemplate }
};

// Custom heading APIs
// Public: fetch an author's heading (global for all themes)
export const getAuthorCustomHeading = async (authorId) => {
  if (!authorId) throw new Error('authorId is required');
  const res = await API.get(`/users/${authorId}/custom-heading`);
  return res.data; // { heading }
};

// Authenticated: set current user's heading (global for all themes)
export const setMyCustomHeading = async (heading, token) => {
  const res = await API.put(
    '/users/me/custom-heading',
    { heading },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data; // { heading }
};

// Custom categories APIs
// Get current user's custom categories
export const getMyCustomCategories = async (token) => {
  const res = await API.get('/users/me/custom-categories', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data; // { categories: [] }
};

// Add a custom category
export const addMyCustomCategory = async (category, token) => {
  const res = await API.post(
    '/users/me/custom-categories',
    { category },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data; // { categories: [], message: '...' }
};

// Remove a custom category
export const removeMyCustomCategory = async (category, token) => {
  // Encode the category name for URL
  const encodedCategory = encodeURIComponent(category);
  const res = await API.delete(`/users/me/custom-categories/${encodedCategory}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data; // { categories: [], message: '...' }
};

// Unfollow user
export const unfollowUser = async (userId, token, currentUserId = "") => {
  try {
    const response = await API.post(
      `/user/${userId}/unfollow`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (currentUserId) {
      patchFollowingIdsCache(currentUserId, userId, false);
    }
    return response.data;
  } catch (error) {
    console.error("Error unfollowing user:", error);
    throw error;
  }
};

export const getUserProfile = async (userId, token) => {
  try {
    const response = await API.get(`/users/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

// Only allow updating allowed fields and always use current user id from auth context
export const updateProfile = async (currentUser, form, token) => {
  try {
    const allowedFields = ['name', 'email', 'bio', 'photo'];
    const formData = new FormData();
    for (const field of allowedFields) {
      if (form[field] !== undefined && form[field] !== null) {
        formData.append(field, form[field]);
      }
    }
    const response = await API.put(`/users/${currentUser.id || currentUser}`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error updating profile:", error);
    throw error;
  }
};

// Favorites functionality temporarily disabled
// Will be re-implemented when backend is ready

/**
 * Notify backend when user views a policy page
 * @param {string} policyType - 'privacy' or 'terms'
 * @param {string} token - Optional auth token (if user is logged in)
 * @returns {Promise<Object>} Response data
 */
export const notifyPolicyView = async (policyType, token = null) => {
  try {
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    const response = await API.post(
      '/users/notify-policy-view',
      { policyType },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error notifying policy view:", error);
    // Don't throw error - this is a non-critical notification
    return { success: false, error: error.message };
  }
};
